// utils/dateUtils.ts
import { DateRange, TimeGrouping } from '../types/analysis.types';
import { MONTH_NAMES } from './constants';

export const formatDateToString = (date: Date): string => {
    const localDate = new Date(date);
    localDate.setHours(12, 0, 0, 0);

    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
};

export const getDefaultDateRange = (): DateRange => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();

    return {
        start: `${currentYear}-01-01`,
        end: `${currentYear}-12-31`
    };
};

export const getYearsInRange = (startDate: string, endDate: string): number[] => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const years: number[] = [];

    for (let year = start.getFullYear(); year <= end.getFullYear(); year++) {
        years.push(year);
    }

    return years;
};

export const getISOWeekNumber = (date: Date): number => {
    const target = new Date(date.valueOf());
    const dayNr = (date.getDay() + 6) % 7;
    target.setDate(target.getDate() - dayNr + 3);
    const firstThursday = target.valueOf();
    target.setMonth(0, 1);
    if (target.getDay() !== 4) {
        target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
    }
    return 1 + Math.ceil((firstThursday - target.valueOf()) / 604800000);
};

export const getGroupKey = (dateStr: string, grouping: TimeGrouping): string => {
    const date = new Date(dateStr);

    switch (grouping) {
        case 'weekly':
            const year = date.getFullYear();
            const week = getISOWeekNumber(date);
            return `${year}-W${week.toString().padStart(2, '0')}`;
        case 'monthly':
            return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
        case 'daily':
        default:
            return dateStr;
    }
};

export const generateAllDateKeys = (minDate: Date, maxDate: Date, grouping: TimeGrouping): string[] => {
    const keys: string[] = [];
    const currentDate = new Date(minDate);

    switch (grouping) {
        case 'weekly':
            const dayOfWeek = currentDate.getDay();
            const daysSinceMonday = (dayOfWeek + 6) % 7;
            currentDate.setDate(currentDate.getDate() - daysSinceMonday);

            while (currentDate <= maxDate) {
                const year = currentDate.getFullYear();
                const week = getISOWeekNumber(currentDate);
                keys.push(`${year}-W${week.toString().padStart(2, '0')}`);
                currentDate.setDate(currentDate.getDate() + 7);
            }
            break;
        case 'monthly':
            currentDate.setDate(1);

            while (currentDate <= maxDate) {
                keys.push(`${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}`);
                currentDate.setMonth(currentDate.getMonth() + 1);
            }
            break;
        case 'daily':
        default:
            while (currentDate <= maxDate) {
                keys.push(currentDate.toISOString().slice(0, 10));
                currentDate.setDate(currentDate.getDate() + 1);
            }
            break;
    }

    return keys;
};

export const formatDateLabel = (dateStr: string, timeGrouping: TimeGrouping): string => {
    if (timeGrouping === 'weekly') {
        const [year, weekPart] = dateStr.split('-W');
        return `v. ${weekPart}`;
    } else if (timeGrouping === 'monthly') {
        const [year, month] = dateStr.split('-');
        return `${MONTH_NAMES[parseInt(month) - 1]} ${year}`;
    } else {
        return dateStr;
    }
};

export const formatDateTime = (dateString: string): string => {
    const [date, time] = dateString.split(' ');
    if (!time) return date;
    const [year, month, day] = date.split('-');
    const formattedDate = `${year}-${month}-${day}`;
    return `${formattedDate} ${time}`;
};

export const formatDate = (dateString: string): string => {
    const [date] = dateString.split(' ');
    return date;
};